import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InputSwitch } from 'primereact/inputswitch';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import BusinessActions from '../../Redux/actions/business-actions';
import CustomToast from '../../Components/CustomToast';

export const DuplicatePhonePreference = () => {
    const { t } = useTranslation('common');
    const dispatch = useDispatch();
    const toggleIntentRef = useRef(null);

    const {
        business,
        togglingAllowDuplicatePhone,
        errorTogglingAllowDuplicatePhone,
    } = useSelector((state) => state.businesses);

    const businessId = business?.id;

    const [allowDuplicatePhone, setAllowDuplicatePhone] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [toastData, setToastData] = useState({ summary: '', severity: '', detail: '', life: 0 });
    const [toastTrigger, setToastTrigger] = useState(false);

    const getBusiness = useCallback((id) => dispatch(BusinessActions.businessRequest(id)), [dispatch]);
    const toggleAllowDuplicatePhone = useCallback(
        (id) => dispatch(BusinessActions.toggleAllowDuplicatePhoneRequest(id)),
        [dispatch]
    );

    const showToast = useCallback((severity, detail) => {
        setToastData({
            summary: severity === 'success' ? t('success') : t('err'),
            severity,
            detail,
            life: 3000,
        });
        setToastTrigger(true);
    }, [t]);

    useEffect(() => {
        if (!business) return;
        setAllowDuplicatePhone(business.allowDuplicatePhone ?? false);
    }, [business]);

    useEffect(() => {
        if (togglingAllowDuplicatePhone) return;

        const intent = toggleIntentRef.current;
        if (!intent) return;

        if (errorTogglingAllowDuplicatePhone) {
            showToast('error', t('duplicatePhone.error_info'));
            toggleIntentRef.current = null;
            setShowConfirm(false);
            setAllowDuplicatePhone(business?.allowDuplicatePhone ?? false);
            return;
        }

        toggleIntentRef.current = null;
        setShowConfirm(false);
        showToast(
            'success',
            intent === 'enable'
                ? t('duplicatePhone.enable_success')
                : t('duplicatePhone.disable_success')
        );
        getBusiness(businessId);
    }, [
        togglingAllowDuplicatePhone,
        errorTogglingAllowDuplicatePhone,
        business?.allowDuplicatePhone,
        businessId,
        getBusiness,
        showToast,
        t,
    ]);

    const handleSwitch = (e) => {
        setAllowDuplicatePhone(e.value);
        setShowConfirm(true);
    };

    const handleConfirmToggle = () => {
        toggleIntentRef.current = allowDuplicatePhone ? 'enable' : 'disable';
        toggleAllowDuplicatePhone(businessId);
    };

    const revertAndClose = () => {
        if (togglingAllowDuplicatePhone) return;
        setShowConfirm(false);
        setAllowDuplicatePhone(business?.allowDuplicatePhone ?? false);
    };

    const confirmFooter = () => (
        <div className="flex justify-content-end">
            <Button
                label={t('common.no')}
                icon="pi pi-times"
                className="p-button-text"
                onClick={revertAndClose}
                disabled={togglingAllowDuplicatePhone}
            />
            <Button
                label={t('common.ok')}
                icon="pi pi-check"
                loading={togglingAllowDuplicatePhone}
                onClick={handleConfirmToggle}
            />
        </div>
    );

    return (
        <>
            <CustomToast
                {...toastData}
                trigger={toastTrigger}
                handleActionsAfterToast={() => setToastTrigger(false)}
                handleActionsAfterErrorToast={() => setToastTrigger(false)}
            />

            <ConfirmDialog
                visible={showConfirm}
                onHide={revertAndClose}
                header={allowDuplicatePhone
                    ? t('duplicatePhone.enable_header')
                    : t('duplicatePhone.disable_header')}
                footer={confirmFooter()}
                style={{ width: '350px' }}
                message={allowDuplicatePhone
                    ? t('duplicatePhone.enable_confirmation')
                    : t('duplicatePhone.disable_confirmation')}
            />

            <hr className="my-4" />

            <div className="grid">
                <section className="col-3">
                    <p className="font-bold text_line">{t('duplicatePhone.title')}</p>
                </section>

                <section className="col-6">
                    <p className="text_line">{t('duplicatePhone.description')}</p>
                </section>

                <section className="col-3 flex justify-content-end gap-2">
                    <InputSwitch
                        checked={allowDuplicatePhone}
                        onChange={handleSwitch}
                        disabled={togglingAllowDuplicatePhone}
                    />
                    <p className="font-bold text_line">
                        {allowDuplicatePhone ? t('api_settings.on') : t('api_settings.off')}
                    </p>
                </section>
            </div>
        </>
    );
};
